import {
    BENEFIT_TIERS,
    BENEFIT_SECTION_TYPES,
    CARDHOLDER_STATUSES,
    COUNTRIES
} from 'components/Content/BeautyInsider/BeautyInsiderBenefits/constants';
import { buildFeaturedBenefitsSection, buildNextTierSection } from 'components/Content/BeautyInsider/BeautyInsiderBenefits/benefitsContent';
import { APPROVAL_STATUS } from 'constants/CreditCard';
import Empty from 'constants/empty';

const TIER_ALIASES = {
    bi: BENEFIT_TIERS.BI,
    insider: BENEFIT_TIERS.BI,
    beautyinsider: BENEFIT_TIERS.BI,
    ['beauty_insider']: BENEFIT_TIERS.BI,
    vib: BENEFIT_TIERS.VIB,
    rouge: BENEFIT_TIERS.ROUGE,
    nonbi: BENEFIT_TIERS.NON_BI,
    ['non_bi']: BENEFIT_TIERS.NON_BI,
    none: BENEFIT_TIERS.NON_BI
};

const CARDHOLDER_STATUS_ALIASES = {
    cardholder: CARDHOLDER_STATUSES.CARDHOLDER,
    approved: CARDHOLDER_STATUSES.CARDHOLDER,
    ['card_holder']: CARDHOLDER_STATUSES.CARDHOLDER,
    noncardholder: CARDHOLDER_STATUSES.NON_CARDHOLDER,
    ['non_cardholder']: CARDHOLDER_STATUSES.NON_CARDHOLDER,
    ['non_card_holder']: CARDHOLDER_STATUSES.NON_CARDHOLDER
};

const normalizeToken = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, '_');

/**
 * Normalizes API/user tier labels into Benefits tab tier keys.
 *
 * @param {string} value Tier label from user data or client summary.
 * @returns {string|null} Matching Benefits tier key, or null when unknown.
 */
const normalizeTier = value => TIER_ALIASES[normalizeToken(value)] || null;

const getTierFromClientSummary = clientSummary =>
    normalizeTier(clientSummary?.currentTier) || normalizeTier(clientSummary?.tier) || normalizeTier(clientSummary?.vibSegment);

const getTierFromUser = user =>
    normalizeTier(user?.beautyInsiderAccount?.vibSegment) || normalizeTier(user?.beautyInsiderAccount?.tier) || normalizeTier(user?.vibSegment);

/**
 * Resolves the tier that drives Benefits tab sections.
 *
 * @param {Object} options User and client summary data.
 * @returns {string} Benefits tier key.
 */
const resolveTier = ({ user, clientSummary } = Empty.Object) => {
    const userTier = getTierFromUser(user);

    if (userTier) {
        return userTier;
    }

    return getTierFromClientSummary(clientSummary) || BENEFIT_TIERS.NON_BI;
};

const resolveCountry = ({ country, localeCountry } = Empty.Object) =>
    String(country || localeCountry || COUNTRIES.US)
        .trim()
        .toUpperCase();

const hasBankRewardsSignal = bankRewards =>
    Boolean(
        bankRewards?.ccRewardStatus ||
            bankRewards?.rewardCertificates?.length ||
            bankRewards?.rewardsTotal > 0 ||
            bankRewards?.ytdRewardsEarned > 0 ||
            bankRewards?.YTDRewardsEarned > 0
    );

const getCcApprovalStatus = user => user?.beautyInsiderAccount?.ccAccountandPrescreenInfo?.ccApprovalStatus;

/**
 * Resolves whether the current Benefits state should include cardholder-only content.
 *
 * @param {Object} options Cardholder flags and user credit-card data.
 * @returns {string} Cardholder status key.
 */
const resolveCardholderStatus = ({ cardholderStatus, isCardholder, user } = Empty.Object) => {
    const hasCardholderStatus = cardholderStatus !== undefined && cardholderStatus !== null && cardholderStatus !== '';
    const normalizedStatus = CARDHOLDER_STATUS_ALIASES[normalizeToken(cardholderStatus)];

    if (normalizedStatus) {
        return normalizedStatus;
    }

    if (hasCardholderStatus) {
        return CARDHOLDER_STATUSES.NON_CARDHOLDER;
    }

    if (typeof isCardholder === 'boolean') {
        return isCardholder ? CARDHOLDER_STATUSES.CARDHOLDER : CARDHOLDER_STATUSES.NON_CARDHOLDER;
    }

    if (getCcApprovalStatus(user) === APPROVAL_STATUS.APPROVED || user?.ccCardType || hasBankRewardsSignal(user?.ccRewards?.bankRewards)) {
        return CARDHOLDER_STATUSES.CARDHOLDER;
    }

    return CARDHOLDER_STATUSES.NON_CARDHOLDER;
};

/**
 * Builds the Benefits tab view state from user, summary, locale, and cardholder data.
 *
 * @param {Object} options Raw data needed by Benefits tab presentation sections.
 * @returns {Object} Normalized Benefits tab state.
 */
const mapBenefitsViewState = (options = Empty.Object) => {
    const tier = resolveTier(options);
    const country = resolveCountry(options);
    const cardholderStatus = resolveCardholderStatus(options);
    const isCanada = country === COUNTRIES.CA;
    const featuredBenefits = buildFeaturedBenefitsSection(tier);
    const nextTier = buildNextTierSection(tier);
    const sections = [featuredBenefits, nextTier];

    return {
        tier,
        country,
        cardholderStatus,
        isCardholder: !isCanada && cardholderStatus === CARDHOLDER_STATUSES.CARDHOLDER,
        isSignedInTier: tier !== BENEFIT_TIERS.NON_BI,
        sections: sections.filter(Boolean),
        featuredBenefits,
        nextTier
    };
};

export {
    mapBenefitsViewState, normalizeTier, resolveTier, resolveCardholderStatus, BENEFIT_SECTION_TYPES
};

export default mapBenefitsViewState;
